import { Component, OnInit, ViewChild, TemplateRef, Inject, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {IrbInitialSetupService} from "@irb/irb-initial-setup/irb-initial-setup.service";
import {AppService} from "@irb/app.service";
import {IrbAppEvents} from "@irb/app.events";
import {IAlertDialogData} from "@irb/app.model";
import { IGlobalSettings } from '@irb/irb-settings/irb-settings.model';
import { UtilityService } from '@irb/irb-common/services/utility.service';
import { Observable, of } from 'rxjs';

export interface DialogInitialSettingsConfig {
  isReadOnly: boolean;
  onProceed: Function;
  onCancel: Function;
}

@Component({ standalone: false,
  selector: 'irb-dialog-initial-setup',
  templateUrl: './dialog-initial-setup.component.html',
  styleUrls: ['./dialog-initial-setup.component.scss']
})
export class DialogInitialSetupComponent implements OnInit {
  @ViewChild('taxableCashMinPerc') taxableCashMinPerc: any;
  @ViewChild('taxableCashMinDollarAmnt') taxableCashMinDollarAmnt: any;
  @ViewChild('taxableCashTargetPerc') taxableCashTargetPerc: any;
  @ViewChild('taxableCashMaxPerc') taxableCashMaxPerc: any;
  @ViewChild('qualifiedCashMinPerc') qualifiedCashMinPerc: any;
  @ViewChild('qualifiedCashMinDollarAmnt') qualifiedCashMinDollarAmnt: any;
  @ViewChild('qualifiedCashTargetPerc') qualifiedCashTargetPerc: any;
  @ViewChild('qualifiedCashMaxPerc') qualifiedCashMaxPerc: any;
  @ViewChild('reinvestUnsupportedConfigMessage') reinvestUnsupportedConfigMessage: TemplateRef<any>;
  adminSettings: IGlobalSettings;
  originalAdminSettings: IGlobalSettings;
  isModified: boolean = false;
  isValid: boolean = true;
  cashSettingsPlaceholder: string = '';
  uiReady: boolean = false;
  validationErrors: string[];
  taxableCashRequired: boolean = false;
  qualifiedCashRequired: boolean = false;

  constructor(public dialogRef: MatDialogRef<DialogInitialSetupComponent>,
              @Inject(MAT_DIALOG_DATA) public mdd: DialogInitialSettingsConfig,
              private uas: IrbInitialSetupService,
              private as: AppService,
              private util: UtilityService,
              private cdr: ChangeDetectorRef
            ) { }

  ngOnInit(): void {
    this.getAdminSettings();
  }

  proceed() {
    if(this.mdd.onProceed) {
        this.mdd.onProceed();
    }

    this.dialogRef.close();
  }

  cancel() {
    if(this.mdd.onCancel) {
      this.mdd.onCancel();
    }

    this.dialogRef.close();
  }

  getAdminSettings(){
    if(!this.mdd.isReadOnly) {
      this.uas.getAdminSettings().subscribe((data: any) => {
          this.adminSettings = data as IGlobalSettings;
          this.originalAdminSettings = this.util.clone(this.adminSettings);
          
          //default reinvestments
          this.adminSettings.isDividendReinvestTaxable = true;
          this.adminSettings.isCapGainsReinvestTaxable = true;
          this.adminSettings.isDividendReinvestQualified = true;
          this.adminSettings.isCapGainsReinvestQualified = true;

          this.setDefaultCashSettings();
          this.onInputChange();

          setTimeout(() => { 
            this.uiReady = true; 
            this.cdr.detectChanges();
          }, 2000);
      });
    }
  }

  saveItem(){
    this.validateInput().subscribe(validationErrors => {
        this.isValid = validationErrors.length === 0;

        if(this.isValid) {
          this.uas.setAdminSettings(this.adminSettings).subscribe((res: any) => {
              if (res.responseStatus === 'SUCCESS') {
                this.proceed();
              }
          });
        }
    });
  }

  validateInput(): Observable<string[]> {
      this.validationErrors = [];
  
      if(!this.adminSettings.useModelCash) {
        if(!this.taxableCashMinPerc?.inputControl.valid) {
          this.validationErrors.push("");
      }
  
        if(!this.taxableCashMinDollarAmnt?.inputControl.valid) {
          this.validationErrors.push("");
      }
  
        if(!this.taxableCashMaxPerc?.inputControl.valid) {
          this.validationErrors.push("");
      }
  
        if(!this.qualifiedCashMinPerc?.inputControl.valid) {
          this.validationErrors.push("");
      }
  
        if(!this.taxableCashTargetPerc?.inputControl.valid) {
          this.validationErrors.push("");
      }
  
        if(!this.qualifiedCashTargetPerc?.inputControl.valid) {
          this.validationErrors.push("");
      }
  
        if(!this.qualifiedCashMinDollarAmnt?.inputControl.valid) {
          this.validationErrors.push("");
      }
  
        if(!this.qualifiedCashMaxPerc?.inputControl.valid) {
          this.validationErrors.push("");
      }
      }
  
      let b = this.adminSettings.isDividendReinvestTaxable === null || this.adminSettings.isDividendReinvestQualified === null || this.adminSettings.isCapGainsReinvestTaxable === null || this.adminSettings.isCapGainsReinvestQualified === null || this.adminSettings.isAddToNetAmount === null;

      if(b) {
        this.validationErrors.push("");
      }

      return of(this.validationErrors);
  }

  validateReinvestChange() {
      const isTaxableReinvestValid = !(this.adminSettings.isDividendReinvestTaxable === true && this.adminSettings.isCapGainsReinvestTaxable === false);
      const isQualifiedReinvestValid = !(this.adminSettings.isDividendReinvestQualified === true && this.adminSettings.isCapGainsReinvestQualified === false);

      if(!isTaxableReinvestValid || !isQualifiedReinvestValid) {
          this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
              msg: "You have selected an unsupported reinvestment election.",
              altMsgTemplate: this.reinvestUnsupportedConfigMessage,
              dType: "WARN",
              onProceed: () => this.revertReinvestChange(),
              onCancel: () => this.revertReinvestChange()
          } as IAlertDialogData);
      } else {
        this.onInputChange();
      }
  }

  revertReinvestChange() {
    const isTaxableReinvestValid = !(this.adminSettings.isDividendReinvestTaxable === true && this.adminSettings.isCapGainsReinvestTaxable === false);
    const isQualifiedReinvestValid = !(this.adminSettings.isDividendReinvestQualified === true && this.adminSettings.isCapGainsReinvestQualified === false);

    setTimeout(() => {
        if(!isTaxableReinvestValid) {
            this.adminSettings.isCapGainsReinvestTaxable = true;
            this.onInputChange();
        }

        if(!isQualifiedReinvestValid) {
            this.adminSettings.isCapGainsReinvestQualified = true;
            this.onInputChange();
        }
    });
  }

  onInputChange() {
    this.isModified = !this.util.compare(this.adminSettings, this.originalAdminSettings);

    this.validateInput().subscribe(validationErrors => {
        this.isValid = validationErrors.length === 0;
    });

    this.taxableCashRequired = this.adminSettings.taxableCashMinPerc != null 
      || this.adminSettings.taxableCashMinDollarAmnt != null
      || this.adminSettings.taxableCashTargetPerc != null 
      || this.adminSettings.taxableCashMaxPerc != null;

    this.qualifiedCashRequired = this.adminSettings.qualifiedCashMinPerc != null 
      || this.adminSettings.qualifiedCashMinDollarAmnt != null 
      || this.adminSettings.qualifiedCashTargetPerc != null 
      || this.adminSettings.qualifiedCashMaxPerc != null;
  }

  setDefaultCashSettings() {
     if(this.adminSettings.useModelCash) {
        this.cashSettingsPlaceholder = "n/a";
        this.adminSettings.taxableCashMinPerc = null;
        this.adminSettings.taxableCashMaxPerc = null;
        this.adminSettings.taxableCashTargetPerc = null;
        this.adminSettings.taxableCashMinDollarAmnt = null;
        this.adminSettings.qualifiedCashMinPerc = null;
        this.adminSettings.qualifiedCashMaxPerc = null;
        this.adminSettings.qualifiedCashTargetPerc = null;
        this.adminSettings.qualifiedCashMinDollarAmnt = null;
      } else {
        this.cashSettingsPlaceholder = "";
        this.adminSettings.taxableCashMinPerc = this.adminSettings.taxableCashMinPerc || 0;
        this.adminSettings.taxableCashMaxPerc = this.adminSettings.taxableCashMaxPerc || 0;
        this.adminSettings.taxableCashTargetPerc = this.adminSettings.taxableCashTargetPerc || 0;
        this.adminSettings.taxableCashMinDollarAmnt = this.adminSettings.taxableCashMinDollarAmnt || 0;
        this.adminSettings.qualifiedCashMinPerc = this.adminSettings.qualifiedCashMinPerc || 0;
        this.adminSettings.qualifiedCashMaxPerc = this.adminSettings.qualifiedCashMaxPerc || 0;
        this.adminSettings.qualifiedCashTargetPerc = this.adminSettings.qualifiedCashTargetPerc || 0;
        this.adminSettings.qualifiedCashMinDollarAmnt = this.adminSettings.qualifiedCashMinDollarAmnt || 0;
      }

      this.cdr.detectChanges();
  }

  onUseModelCashChange() {
    this.onInputChange();
    setTimeout(() => { this.setDefaultCashSettings(); });
  }
}
